import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, ArrowRight, ArrowLeft, Gift, CreditCard, CheckCircle,
  Ban, Lock, Eye, AlertTriangle, ShieldOff, DollarSign, Shield,
  XCircle, Database, Package, Scale, HelpCircle, Upload, Camera,
  Link2, User, Mail, Globe, MapPin, Sparkles, Image, X, EyeOff, MessageSquareOff
} from 'lucide-react';
import { FaMeta, FaGoogle, FaAmazon, FaTiktok, FaSnapchat, FaApple, FaMicrosoft, FaSpotify } from 'react-icons/fa6';
import { FaInstagram, FaYoutube, FaTwitter, FaLinkedin, FaPinterest, FaReddit, FaDiscord, FaTelegram, FaWhatsapp, FaPaypal, FaEbay, FaAirbnb, FaTwitch, FaSteam, FaSlack, FaShopify, FaStripeS, FaDropbox, FaEtsy } from 'react-icons/fa';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { appealsAPI, paymentsAPI, plansAPI, platformFieldsAPI } from '../lib/api';
import { toast } from 'sonner';
import { useTheme } from '../App';

/* ═══════════════════════════════════════════════════════
   ISSUE TYPES - 3D Cards with Icons & Colors
   ═══════════════════════════════════════════════════════ */
const ISSUE_TYPES = [
  { id: 'Account Banned', label: 'Account Banned', labelAr: 'حظر الحساب', labelDe: 'Konto gesperrt', icon: Ban, color: '#EF4444', bg: 'rgba(239,68,68,0.08)' },
  { id: 'Account Locked', label: 'Account Locked', labelAr: 'قفل الحساب', labelDe: 'Konto gesperrt', icon: Lock, color: '#F59E0B', bg: 'rgba(245,158,11,0.08)' },
  { id: 'Content Removed', label: 'Content Removed', labelAr: 'إزالة المحتوى', labelDe: 'Inhalt entfernt', icon: Eye, color: '#8B5CF6', bg: 'rgba(139,92,246,0.08)' },
  { id: 'Account Suspended', label: 'Account Suspended', labelAr: 'تعليق الحساب', labelDe: 'Konto suspendiert', icon: AlertTriangle, color: '#F97316', bg: 'rgba(249,115,22,0.08)' },
  { id: 'Shadow Banned', label: 'Shadow Banned', labelAr: 'حظر خفي', labelDe: 'Shadow Ban', icon: EyeOff, color: '#6366F1', bg: 'rgba(99,102,241,0.08)' },
  { id: 'Demonetized', label: 'Demonetized', labelAr: 'إلغاء الربح', labelDe: 'Demonetisiert', icon: DollarSign, color: '#10B981', bg: 'rgba(16,185,129,0.08)' },
  { id: 'Restricted Access', label: 'Restricted Access', labelAr: 'وصول مقيد', labelDe: 'Eingeschränkter Zugang', icon: ShieldOff, color: '#EC4899', bg: 'rgba(236,72,153,0.08)' },
  { id: 'Failed Appeal', label: 'Failed Appeal', labelAr: 'طعن مرفوض', labelDe: 'Fehlgeschlagener Einspruch', icon: XCircle, color: '#EF4444', bg: 'rgba(239,68,68,0.08)' },
  { id: 'Data Request Denied', label: 'Data Request Denied', labelAr: 'رفض طلب البيانات', labelDe: 'Datenanfrage abgelehnt', icon: Database, color: '#06B6D4', bg: 'rgba(6,182,212,0.08)' },
  { id: 'Product Return', label: 'Product Return', labelAr: 'إرجاع المنتج', labelDe: 'Produktrückgabe', icon: Package, color: '#84CC16', bg: 'rgba(132,204,22,0.08)' },
  { id: 'Unfair Removal', label: 'Unfair Removal', labelAr: 'إزالة غير عادلة', labelDe: 'Ungerechtfertigte Entfernung', icon: Scale, color: '#3B82F6', bg: 'rgba(59,130,246,0.08)' },
  { id: 'Other', label: 'Other', labelAr: 'أخرى', labelDe: 'Sonstiges', icon: HelpCircle, color: '#94A3B8', bg: 'rgba(148,163,184,0.08)' },
];

/* ═══════════════════════════════════════════════════════
   PLATFORMS - Neon Logo Grid (~40 platforms)
   ═══════════════════════════════════════════════════════ */
const LetterIcon = ({ letter, style }) => (
  <span className="text-2xl font-extrabold" style={{ fontFamily: 'Outfit', ...style }}>{letter}</span>
);

const PLATFORMS = [
  { name: 'Facebook', value: 'Facebook', icon: FaMeta, color: '#1877F2' },
  { name: 'Instagram', value: 'Instagram', icon: FaInstagram, color: '#E4405F' },
  { name: 'Google', value: 'Google', icon: FaGoogle, color: '#4285F4' },
  { name: 'YouTube', value: 'YouTube', icon: FaYoutube, color: '#FF0000' },
  { name: 'TikTok', value: 'TikTok', icon: FaTiktok, color: '#00F2EA' },
  { name: 'Twitter/X', value: 'Twitter/X', icon: FaTwitter, color: '#1DA1F2' },
  { name: 'Amazon', value: 'Amazon', icon: FaAmazon, color: '#FF9900' },
  { name: 'Apple', value: 'Apple', icon: FaApple, color: '#A2AAAD' },
  { name: 'Microsoft', value: 'Microsoft', icon: FaMicrosoft, color: '#00BCF2' },
  { name: 'Spotify', value: 'Spotify', icon: FaSpotify, color: '#1DB954' },
  { name: 'LinkedIn', value: 'LinkedIn', icon: FaLinkedin, color: '#0A66C2' },
  { name: 'Snapchat', value: 'Snapchat', icon: FaSnapchat, color: '#FFFC00' },
  { name: 'Pinterest', value: 'Pinterest', icon: FaPinterest, color: '#E60023' },
  { name: 'Reddit', value: 'Reddit', icon: FaReddit, color: '#FF5700' },
  { name: 'Discord', value: 'Discord', icon: FaDiscord, color: '#5865F2' },
  { name: 'Telegram', value: 'Telegram', icon: FaTelegram, color: '#26A5E4' },
  { name: 'WhatsApp', value: 'WhatsApp', icon: FaWhatsapp, color: '#25D366' },
  { name: 'PayPal', value: 'PayPal', icon: FaPaypal, color: '#00457C' },
  { name: 'eBay', value: 'eBay', icon: FaEbay, color: '#E53238' },
  { name: 'Airbnb', value: 'Airbnb', icon: FaAirbnb, color: '#FF5A5F' },
  { name: 'Twitch', value: 'Twitch', icon: FaTwitch, color: '#9146FF' },
  { name: 'Steam', value: 'Steam', icon: FaSteam, color: '#00ADEE' },
  { name: 'Slack', value: 'Slack', icon: FaSlack, color: '#4A154B' },
  { name: 'Shopify', value: 'Shopify', icon: FaShopify, color: '#96BF48' },
  { name: 'Stripe', value: 'Stripe', icon: FaStripeS, color: '#635BFF' },
  { name: 'Dropbox', value: 'Dropbox', icon: FaDropbox, color: '#0061FF' },
  { name: 'Etsy', value: 'Etsy', icon: FaEtsy, color: '#F16521' },
  { name: 'Netflix', value: 'Netflix', icon: null, letter: 'N', color: '#E50914' },
  { name: 'Uber', value: 'Uber', icon: null, letter: 'U', color: '#000000' },
  { name: 'Zoom', value: 'Zoom', icon: null, letter: 'Z', color: '#2D8CFF' },
  { name: 'Adobe', value: 'Adobe', icon: null, letter: 'A', color: '#FF0000' },
  { name: 'Salesforce', value: 'Salesforce', icon: null, letter: 'S', color: '#00A1E0' },
  { name: 'Booking', value: 'Booking', icon: null, letter: 'B', color: '#003580' },
  { name: 'Fiverr', value: 'Fiverr', icon: null, letter: 'F', color: '#1DBF73' },
  { name: 'Alibaba', value: 'Alibaba', icon: null, letter: 'A', color: '#FF6A00' },
  { name: 'Wish', value: 'Wish', icon: null, letter: 'W', color: '#2FB7EC' },
  { name: 'PlayStation', value: 'PlayStation', icon: null, letter: 'P', color: '#003087' },
  { name: 'Xbox', value: 'Xbox', icon: null, letter: 'X', color: '#107C10' },
  { name: 'Nintendo', value: 'Nintendo', icon: null, letter: 'N', color: '#E60012' },
  { name: 'Samsung', value: 'Samsung', icon: null, letter: 'S', color: '#1428A0' },
];

const COUNTRIES = [
  'Germany', 'France', 'Italy', 'Spain', 'Netherlands', 'Belgium', 'Austria',
  'Poland', 'Sweden', 'Denmark', 'Finland', 'Ireland', 'Portugal', 'Greece',
  'Czech Republic', 'Romania', 'Hungary', 'Bulgaria', 'Croatia', 'Slovakia',
  'Slovenia', 'Lithuania', 'Latvia', 'Estonia', 'Luxembourg', 'Malta', 'Cyprus'
];

/* ═══════════════════════════════════════════════════════
   SMART FORM FIELDS BY ISSUE TYPE
   ═══════════════════════════════════════════════════════ */
const SMART_FIELDS = {
  'Account Banned': {
    description_hint: 'Briefly describe when and why your account was banned...',
    fields: [
      { name: 'account_url', label: 'Account/Profile URL', type: 'url', icon: Link2, required: true, placeholder: 'https://...' },
      { name: 'username', label: 'Username on Platform', type: 'text', icon: User, required: true, placeholder: '@username' },
    ]
  },
  'Account Locked': {
    description_hint: 'Describe the lock situation and any verification steps you tried...',
    fields: [
      { name: 'account_url', label: 'Account URL', type: 'url', icon: Link2, required: true, placeholder: 'https://...' },
      { name: 'username', label: 'Username', type: 'text', icon: User, required: true, placeholder: '@username' },
    ]
  },
  'Content Removed': {
    description_hint: 'Describe the removed content and the reason given...',
    fields: [
      { name: 'content_url', label: 'Content URL', type: 'url', icon: Link2, required: true, placeholder: 'https://...' },
      { name: 'content_title', label: 'Content Title', type: 'text', icon: FileText, placeholder: 'Title of removed content' },
    ]
  },
  'Account Suspended': {
    description_hint: 'Describe the suspension and any notices received...',
    fields: [
      { name: 'account_url', label: 'Account URL', type: 'url', icon: Link2, required: true, placeholder: 'https://...' },
      { name: 'username', label: 'Username', type: 'text', icon: User, required: true, placeholder: '@username' },
    ]
  },
  'Shadow Banned': {
    description_hint: 'Describe how you noticed the shadow ban and its effects...',
    fields: [
      { name: 'account_url', label: 'Account URL', type: 'url', icon: Link2, required: true, placeholder: 'https://...' },
      { name: 'username', label: 'Username', type: 'text', icon: User, required: true, placeholder: '@username' },
    ]
  },
  'Demonetized': {
    description_hint: 'Describe the demonetization notice and affected content...',
    fields: [
      { name: 'account_url', label: 'Channel/Account URL', type: 'url', icon: Link2, required: true, placeholder: 'https://...' },
      { name: 'username', label: 'Channel/Account Name', type: 'text', icon: User, required: true, placeholder: 'Channel name' },
    ]
  },
  'Restricted Access': {
    description_hint: 'Describe what features or access was restricted...',
    fields: [
      { name: 'account_url', label: 'Account URL', type: 'url', icon: Link2, required: true, placeholder: 'https://...' },
    ]
  },
  'Failed Appeal': {
    description_hint: 'Describe the original issue and the failed appeal response...',
    fields: [
      { name: 'account_url', label: 'Account URL', type: 'url', icon: Link2, required: true, placeholder: 'https://...' },
      { name: 'previous_appeal_ref', label: 'Previous Appeal Reference', type: 'text', icon: FileText, placeholder: 'Reference number if available' },
    ]
  },
  'Data Request Denied': {
    description_hint: 'Describe the data request and the denial reason...',
    fields: [
      { name: 'account_url', label: 'Account URL', type: 'url', icon: Link2, required: true, placeholder: 'https://...' },
    ]
  },
  'Product Return': {
    description_hint: 'Describe the product and the issue with the return...',
    fields: [
      { name: 'order_url', label: 'Order URL', type: 'url', icon: Link2, required: true, placeholder: 'https://...' },
      { name: 'order_id', label: 'Order ID', type: 'text', icon: Package, placeholder: 'Order number' },
    ]
  },
  'Unfair Removal': {
    description_hint: 'Describe what was removed and why you believe it was unfair...',
    fields: [
      { name: 'content_url', label: 'Content/Account URL', type: 'url', icon: Link2, required: true, placeholder: 'https://...' },
    ]
  },
  'Other': {
    description_hint: 'Describe your issue in detail...',
    fields: [
      { name: 'account_url', label: 'Related URL', type: 'url', icon: Link2, placeholder: 'https://...' },
    ]
  },
};

/* ═══════════════════════════════════════════════════════
   PAGE TRANSITION VARIANTS
   ═══════════════════════════════════════════════════════ */
const pageVariants = {
  enter: { opacity: 0, x: 60, scale: 0.98 },
  center: { opacity: 1, x: 0, scale: 1 },
  exit: { opacity: 0, x: -60, scale: 0.98 },
};
const pageTransition = { duration: 0.5, ease: [0.23, 1, 0.32, 1] };

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════ */
export default function SubmitRequestPage({ user }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const isDark = theme !== 'aurora';
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [useFree, setUseFree] = useState(false);
  const [dynamicFields, setDynamicFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [screenshots, setScreenshots] = useState([]);
  const [contentLinks, setContentLinks] = useState(['']);
  const [otherPlatform, setOtherPlatform] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const [form, setForm] = useState({
    platform: '', issue_type: location.state?.issueType || '',
    description: '', full_name: user?.name || '', email: user?.email || '', country: ''
  });

  const totalSteps = 5;

  useEffect(() => {
    plansAPI.list().then(res => { setPlans(res.data); if (res.data.length > 0) setSelectedPlan(res.data[0]); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.platform && form.platform !== 'Other') {
      platformFieldsAPI.list(form.platform).then(res => {
        setDynamicFields(res.data);
        setCustomFieldValues({});
      }).catch(() => setDynamicFields([]));
    } else {
      setDynamicFields([]);
    }
  }, [form.platform]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const validLinks = contentLinks.filter(l => l.trim());
      const submitData = {
        ...form,
        platform: form.platform === 'Other' ? otherPlatform : form.platform,
        custom_fields: {
          ...customFieldValues,
          screenshots: screenshots.map(s => s.name).join(', '),
          ...(validLinks.length > 0 ? { content_links: validLinks.join(' | ') } : {}),
        }
      };
      const res = await appealsAPI.create(submitData);

      if (useFree) {
        try {
          await paymentsAPI.submitFree(res.data.id);
          toast.success(`Appeal submitted! Tracking: ${res.data.tracking_code}`);
          navigate(`/track/${res.data.tracking_code}`);
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Free appeal limit reached');
          setUseFree(false);
        }
      } else {
        const checkoutRes = await paymentsAPI.checkout({ plan_id: selectedPlan?.id || null, appeal_id: res.data.id, origin_url: window.location.origin });
        toast.success(`Tracking: ${res.data.tracking_code}. Redirecting to payment...`);
        window.location.href = checkoutRes.data.url;
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setLoading(false); }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    addFiles(files);
  };

  const addFiles = (files) => {
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    const newScreenshots = imageFiles.map(f => ({
      file: f,
      name: f.name,
      preview: URL.createObjectURL(f),
    }));
    setScreenshots(prev => [...prev, ...newScreenshots].slice(0, 5));
  };

  const removeScreenshot = (index) => {
    setScreenshots(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDragIn = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const handleDragOut = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const getFieldLabel = (f) => {
    if (i18n.language === 'ar' && f.field_label_ar) return f.field_label_ar;
    if (i18n.language === 'de' && f.field_label_de) return f.field_label_de;
    return f.field_label;
  };

  const getIssueLabel = (issue) => {
    if (i18n.language === 'ar') return issue.labelAr;
    if (i18n.language === 'de') return issue.labelDe;
    return issue.label;
  };

  const goNext = () => setStep(s => Math.min(s + 1, totalSteps));
  const goBack = () => setStep(s => Math.max(s - 1, 1));

  // Style helpers
  const inputCl = isDark ? 'bg-white/[0.03] border-white/[0.08] text-white focus:border-blue-500/40' : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-400';
  const dropCl = isDark ? 'bg-[#0A0A0C] border-white/10' : 'bg-white border-gray-200';
  const itemCl = isDark ? 'text-white hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50';
  const textP = isDark ? 'text-white' : 'text-gray-900';
  const textS = isDark ? 'text-white/60' : 'text-gray-500';
  const textM = isDark ? 'text-white/40' : 'text-gray-400';
  const cardBg = isDark ? 'bg-[#0A0A0C]/60 backdrop-blur-2xl border-white/[0.06]' : 'bg-white/90 backdrop-blur-xl border-gray-200 shadow-xl shadow-gray-200/50';

  const smartFields = SMART_FIELDS[form.issue_type] || SMART_FIELDS['Other'];

  const STEP_LABELS = [
    i18n.language === 'ar' ? 'نوع المشكلة' : i18n.language === 'de' ? 'Problemtyp' : 'Issue Type',
    i18n.language === 'ar' ? 'المنصة' : i18n.language === 'de' ? 'Plattform' : 'Platform',
    i18n.language === 'ar' ? 'التفاصيل' : i18n.language === 'de' ? 'Details' : 'Details',
    i18n.language === 'ar' ? 'المعلومات' : i18n.language === 'de' ? 'Persönlich' : 'Personal',
    i18n.language === 'ar' ? 'الدفع' : i18n.language === 'de' ? 'Zahlung' : 'Payment',
  ];

  return (
    <div className={`min-h-screen relative overflow-hidden transition-colors ${isDark ? '' : 'bg-[#F8FAFC]'}`}>
      {/* Background */}
      <div className="absolute inset-0 grid-bg-subtle opacity-40 pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] rounded-full pointer-events-none"
        style={{ background: isDark ? 'radial-gradient(ellipse, rgba(59,130,246,0.06) 0%, rgba(139,92,246,0.03) 40%, transparent 70%)' : 'radial-gradient(ellipse, rgba(59,130,246,0.04) 0%, transparent 60%)' }} />

      <div className="relative max-w-4xl mx-auto px-6 py-20">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-4 mb-6">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 200 }}
              className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isDark ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border-2 border-blue-200'}`}
              style={{ boxShadow: isDark ? '0 0 30px rgba(59,130,246,0.12)' : '' }}
            >
              <FileText className="w-7 h-7 text-blue-500" />
            </motion.div>
            <div>
              <h1 className={`text-3xl font-extrabold tracking-tight ${textP}`} style={{ fontFamily: 'Outfit' }}>
                {t('appeal.title')}
              </h1>
              <p className={`text-sm ${textM}`}>
                {i18n.language === 'ar' ? `الخطوة ${step} من ${totalSteps}` : `Step ${step} of ${totalSteps}`}
              </p>
            </div>
          </div>

          {/* Animated Step Progress */}
          <div className="flex items-center gap-2">
            {STEP_LABELS.map((label, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                <div className="w-full h-1.5 rounded-full overflow-hidden relative">
                  <div className={`absolute inset-0 ${isDark ? 'bg-white/[0.04]' : 'bg-gray-200'}`} />
                  <motion.div
                    className="absolute inset-y-0 left-0 rounded-full"
                    initial={false}
                    animate={{
                      width: i + 1 <= step ? '100%' : '0%',
                      background: i + 1 <= step ? 'linear-gradient(90deg, #3B82F6, #8B5CF6)' : 'transparent',
                    }}
                    transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
                    style={i + 1 === step ? { boxShadow: '0 0 10px rgba(59,130,246,0.5)' } : {}}
                  />
                </div>
                <span className={`text-[10px] font-medium tracking-wide hidden md:block ${i + 1 <= step ? (isDark ? 'text-blue-400' : 'text-blue-600') : textM}`}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Step Content with Animated Transitions */}
        <AnimatePresence mode="wait">
          {/* ═══════════════════════════════════════
             STEP 1: ISSUE TYPE SELECTION
             ═══════════════════════════════════════ */}
          {step === 1 && (
            <motion.div key="step1" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={pageTransition}>
              <div className="text-center mb-8">
                <h2 className={`text-xl font-bold ${textP}`} style={{ fontFamily: 'Outfit' }}>
                  {i18n.language === 'ar' ? 'ما نوع مشكلتك؟' : i18n.language === 'de' ? 'Was ist Ihr Problem?' : 'What is your issue?'}
                </h2>
                <p className={`text-sm mt-1 ${textM}`}>
                  {i18n.language === 'ar' ? 'اختر نوع المشكلة التي تواجهها' : i18n.language === 'de' ? 'Wählen Sie die Art des Problems' : 'Select the type of issue you are facing'}
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {ISSUE_TYPES.map((issue, i) => {
                  const isSelected = form.issue_type === issue.id;
                  const IconComp = issue.icon;

                  return (
                    <motion.button
                      key={issue.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      onClick={() => {
                        setForm({ ...form, issue_type: issue.id });
                        setTimeout(goNext, 300);
                      }}
                      className={`issue-card-3d group text-center ${isSelected ? 'selected' : ''}`}
                      style={{ '--card-glow': `${issue.color}40` }}
                    >
                      <div className={`card-face rounded-2xl p-5 border-2 transition-all cursor-pointer ${
                        isSelected
                          ? `border-[${issue.color}]`
                          : isDark ? 'bg-white/[0.02] border-white/[0.06] hover:border-white/[0.12]' : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm'
                      }`}
                        style={isSelected ? {
                          background: issue.bg,
                          borderColor: `${issue.color}50`,
                          boxShadow: `0 0 30px ${issue.color}15, 0 8px 32px rgba(0,0,0,0.1)`,
                        } : {}}
                      >
                        {/* 3D Icon Container */}
                        <div
                          className="w-14 h-14 mx-auto rounded-xl flex items-center justify-center mb-3 transition-all"
                          style={{
                            background: issue.bg,
                            border: `1.5px solid ${issue.color}25`,
                            boxShadow: isSelected ? `0 0 20px ${issue.color}20` : '',
                            transform: 'perspective(400px) rotateX(5deg)',
                          }}
                        >
                          <IconComp className="w-6 h-6" style={{ color: issue.color }} />
                        </div>

                        <p className={`text-sm font-semibold ${isSelected ? '' : textP}`}
                          style={isSelected ? { color: issue.color } : {}}>
                          {getIssueLabel(issue)}
                        </p>

                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute top-2 right-2"
                          >
                            <CheckCircle className="w-5 h-5" style={{ color: issue.color }} />
                          </motion.div>
                        )}
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* ═══════════════════════════════════════
             STEP 2: PLATFORM SELECTION
             ═══════════════════════════════════════ */}
          {step === 2 && (
            <motion.div key="step2" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={pageTransition}>
              <div className="text-center mb-8">
                <h2 className={`text-xl font-bold ${textP}`} style={{ fontFamily: 'Outfit' }}>
                  {i18n.language === 'ar' ? 'اختر المنصة' : i18n.language === 'de' ? 'Wählen Sie die Plattform' : 'Select Platform'}
                </h2>
                <p className={`text-sm mt-1 ${textM}`}>
                  {i18n.language === 'ar' ? 'أي منصة لديك المشكلة معها؟' : i18n.language === 'de' ? 'Mit welcher Plattform haben Sie ein Problem?' : 'Which platform are you having an issue with?'}
                </p>
              </div>

              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 mb-6 max-h-[500px] overflow-y-auto platform-grid pr-1">
                {PLATFORMS.map((platform, i) => {
                  const isSelected = form.platform === platform.value;
                  const PIcon = platform.icon;

                  return (
                    <motion.button
                      key={platform.name}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.02, duration: 0.3 }}
                      onClick={() => {
                        setForm({ ...form, platform: platform.value });
                        setTimeout(goNext, 300);
                      }}
                      className={`platform-logo-neon rounded-xl p-4 border-2 flex flex-col items-center gap-2 cursor-pointer ${
                        isSelected ? 'selected' : isDark ? 'bg-white/[0.02] border-white/[0.06]' : 'bg-white border-gray-200 shadow-sm'
                      }`}
                      style={{
                        '--platform-color': `${platform.color}50`,
                        ...(isSelected ? {
                          background: isDark ? `${platform.color}10` : `${platform.color}08`,
                          borderColor: `${platform.color}50`,
                          boxShadow: `0 0 20px ${platform.color}25, 0 0 40px ${platform.color}10`,
                        } : {}),
                      }}
                    >
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center transition-all"
                        style={{
                          background: `${platform.color}12`,
                          boxShadow: isSelected ? `0 0 15px ${platform.color}30` : '',
                        }}
                      >
                        {PIcon ? (
                          <PIcon className="text-2xl" style={{ color: platform.color }} />
                        ) : (
                          <LetterIcon letter={platform.letter} style={{ color: platform.color }} />
                        )}
                      </div>
                      <span className={`text-xs font-medium text-center leading-tight ${isSelected ? '' : textS}`}
                        style={isSelected ? { color: platform.color } : {}}>
                        {platform.name}
                      </span>
                      {isSelected && (
                        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                          <CheckCircle className="w-4 h-4" style={{ color: platform.color }} />
                        </motion.div>
                      )}
                    </motion.button>
                  );
                })}

                {/* Other Platform */}
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: PLATFORMS.length * 0.02 }}
                  onClick={() => setForm({ ...form, platform: 'Other' })}
                  className={`platform-logo-neon rounded-xl p-4 border-2 flex flex-col items-center gap-2 cursor-pointer ${
                    form.platform === 'Other' ? 'selected' : isDark ? 'bg-white/[0.02] border-white/[0.06]' : 'bg-white border-gray-200 shadow-sm'
                  }`}
                  style={{
                    '--platform-color': 'rgba(148,163,184,0.5)',
                    ...(form.platform === 'Other' ? {
                      background: isDark ? 'rgba(148,163,184,0.08)' : 'rgba(148,163,184,0.06)',
                      borderColor: 'rgba(148,163,184,0.4)',
                    } : {}),
                  }}
                >
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: 'rgba(148,163,184,0.1)' }}>
                    <HelpCircle className="w-6 h-6 text-slate-400" />
                  </div>
                  <span className={`text-xs font-medium ${textS}`}>
                    {i18n.language === 'ar' ? 'أخرى' : i18n.language === 'de' ? 'Andere' : 'Other'}
                  </span>
                </motion.button>
              </div>

              {/* Other Platform Input */}
              <AnimatePresence>
                {form.platform === 'Other' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-6"
                  >
                    <div className={`rounded-xl p-4 border ${cardBg}`}>
                      <Label className={`text-sm mb-2 block ${textS}`}>
                        {i18n.language === 'ar' ? 'اسم المنصة' : i18n.language === 'de' ? 'Plattformname' : 'Platform Name'}
                      </Label>
                      <Input
                        value={otherPlatform}
                        onChange={(e) => setOtherPlatform(e.target.value)}
                        className={`h-12 ${inputCl} rounded-xl`}
                        placeholder={i18n.language === 'ar' ? 'اكتب اسم المنصة...' : 'Type platform name...'}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Navigation */}
              <div className="flex gap-3 mt-4">
                <Button variant="outline" onClick={goBack} className={`flex-1 h-12 rounded-xl ${isDark ? 'border-white/[0.08] text-white/60' : 'border-gray-200 text-gray-500'}`}>
                  <ArrowLeft className="w-4 h-4 mr-2" /> {t('common.back')}
                </Button>
                <Button onClick={goNext} disabled={!form.platform || (form.platform === 'Other' && !otherPlatform.trim())} className="flex-1 h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-xl">
                  {t('common.next')} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* ═══════════════════════════════════════
             STEP 3: ISSUE DETAILS & SCREENSHOTS
             ═══════════════════════════════════════ */}
          {step === 3 && (
            <motion.div key="step3" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={pageTransition}>
              <div className={`rounded-2xl p-8 border ${cardBg}`}>
                {/* Selected Issue & Platform Badge */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {form.issue_type && (() => {
                    const issue = ISSUE_TYPES.find(it => it.id === form.issue_type);
                    if (!issue) return null;
                    return (
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: issue.bg, border: `1px solid ${issue.color}25` }}>
                        <issue.icon className="w-4 h-4" style={{ color: issue.color }} />
                        <span className="text-xs font-medium" style={{ color: issue.color }}>{getIssueLabel(issue)}</span>
                      </div>
                    );
                  })()}
                  {form.platform && (() => {
                    const plat = PLATFORMS.find(p => p.value === form.platform);
                    const color = plat?.color || '#94A3B8';
                    return (
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: `${color}10`, border: `1px solid ${color}25` }}>
                        <Globe className="w-4 h-4" style={{ color }} />
                        <span className="text-xs font-medium" style={{ color }}>{form.platform === 'Other' ? otherPlatform : form.platform}</span>
                      </div>
                    );
                  })()}
                </div>

                <div className="space-y-6">
                  {/* Smart Fields based on Issue Type */}
                  {smartFields.fields.map((field) => (
                    <div key={field.name}>
                      <Label className={`text-sm mb-2 block ${textS}`}>
                        <field.icon className="w-3.5 h-3.5 inline mr-1.5" />
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        type={field.type}
                        value={customFieldValues[field.name] || ''}
                        onChange={e => setCustomFieldValues({ ...customFieldValues, [field.name]: e.target.value })}
                        className={`h-12 ${inputCl} rounded-xl`}
                        placeholder={field.placeholder}
                      />
                    </div>
                  ))}

                  {/* Description */}
                  <div>
                    <Label className={`text-sm mb-2 block ${textS}`}>
                      <MessageSquareOff className="w-3.5 h-3.5 inline mr-1.5" />
                      {t('appeal.description')} <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className={`min-h-[120px] ${inputCl} rounded-xl resize-none`}
                      placeholder={smartFields.description_hint}
                    />
                  </div>

                  {/* Multi Content/Video Links */}
                  <div>
                    <Label className={`text-sm mb-2 block ${textS}`}>
                      <Link2 className="w-3.5 h-3.5 inline mr-1.5" />
                      {i18n.language === 'ar' ? 'روابط المحتوى والفيديوهات' : i18n.language === 'de' ? 'Inhalts- & Video-Links' : 'Content & Video Links'}
                      <span className={`text-xs ml-1 ${textM}`}>
                        ({i18n.language === 'ar' ? 'يمكنك إضافة عدة روابط' : i18n.language === 'de' ? 'mehrere Links möglich' : 'you can add multiple links'})
                      </span>
                    </Label>
                    <div className="space-y-2">
                      {contentLinks.map((link, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="flex items-center gap-2"
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
                            <span className="text-xs font-bold text-blue-500">{i + 1}</span>
                          </div>
                          <Input
                            type="url"
                            value={link}
                            onChange={(e) => {
                              const updated = [...contentLinks];
                              updated[i] = e.target.value;
                              setContentLinks(updated);
                            }}
                            className={`h-11 flex-1 ${inputCl} rounded-xl`}
                            placeholder={`https://... (${i18n.language === 'ar' ? 'رابط المحتوى أو الفيديو' : 'content or video URL'})`}
                          />
                          {contentLinks.length > 1 && (
                            <button
                              onClick={() => setContentLinks(contentLinks.filter((_, idx) => idx !== i))}
                              className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${isDark ? 'hover:bg-red-500/10 text-white/20 hover:text-red-400' : 'hover:bg-red-50 text-gray-300 hover:text-red-400'}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </motion.div>
                      ))}
                    </div>
                    {contentLinks.length < 10 && (
                      <motion.button
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        onClick={() => setContentLinks([...contentLinks, ''])}
                        className={`mt-2 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed transition-all ${
                          isDark ? 'border-white/[0.06] hover:border-blue-500/30 hover:bg-blue-500/5 text-white/30 hover:text-blue-400' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 text-gray-400 hover:text-blue-500'
                        }`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        <span className="text-xs font-medium">
                          {i18n.language === 'ar' ? 'إضافة رابط آخر' : i18n.language === 'de' ? 'Weiteren Link hinzufügen' : 'Add another link'}
                        </span>
                      </motion.button>
                    )}
                  </div>

                  {/* Dynamic Backend Fields */}
                  {dynamicFields.length > 0 && (
                    <div className={`p-4 rounded-xl border space-y-4 ${isDark ? 'bg-white/[0.02] border-white/[0.04]' : 'bg-blue-50/50 border-blue-100'}`}>
                      <p className={`text-xs uppercase tracking-wider flex items-center gap-1.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                        <Sparkles className="w-3 h-3" />
                        {form.platform === 'Other' ? otherPlatform : form.platform} specific fields
                      </p>
                      {dynamicFields.map(field => (
                        <div key={field.id}>
                          <Label className={`text-sm mb-2 block ${textS}`}>{getFieldLabel(field)} {field.required && <span className="text-red-500">*</span>}</Label>
                          {field.field_type === 'textarea' ? (
                            <Textarea value={customFieldValues[field.field_name] || ''} onChange={e => setCustomFieldValues({ ...customFieldValues, [field.field_name]: e.target.value })} className={`min-h-[80px] ${inputCl} rounded-xl resize-none`} />
                          ) : field.field_type === 'select' ? (
                            <Select value={customFieldValues[field.field_name] || ''} onValueChange={v => setCustomFieldValues({ ...customFieldValues, [field.field_name]: v })}>
                              <SelectTrigger className={`h-12 ${inputCl} rounded-xl`}><SelectValue placeholder={`Select ${getFieldLabel(field)}`} /></SelectTrigger>
                              <SelectContent className={dropCl}>{(field.options || []).map(o => (<SelectItem key={o} value={o} className={itemCl}>{o}</SelectItem>))}</SelectContent>
                            </Select>
                          ) : (
                            <Input type={field.field_type} value={customFieldValues[field.field_name] || ''} onChange={e => setCustomFieldValues({ ...customFieldValues, [field.field_name]: e.target.value })} className={`h-12 ${inputCl} rounded-xl`} />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Screenshot Upload - Drag & Drop */}
                  <div>
                    <Label className={`text-sm mb-2 block ${textS}`}>
                      <Camera className="w-3.5 h-3.5 inline mr-1.5" />
                      {i18n.language === 'ar' ? 'لقطات الشاشة' : i18n.language === 'de' ? 'Screenshots' : 'Screenshots'}
                      <span className={`text-xs ml-1 ${textM}`}>(max 5)</span>
                    </Label>
                    <div
                      onDragEnter={handleDragIn}
                      onDragLeave={handleDragOut}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      className={`relative rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-all ${
                        dragActive
                          ? 'drop-zone-active border-blue-500/40'
                          : isDark ? 'border-white/[0.08] hover:border-white/[0.15] bg-white/[0.01]' : 'border-gray-200 hover:border-gray-300 bg-gray-50/50'
                      }`}
                    >
                      <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileChange} className="hidden" />
                      <motion.div
                        animate={dragActive ? { scale: 1.1, y: -4 } : { scale: 1, y: 0 }}
                        className="inline-flex flex-col items-center gap-2"
                      >
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
                          <Upload className="w-6 h-6 text-blue-500" />
                        </div>
                        <p className={`text-sm font-medium ${textS}`}>
                          {i18n.language === 'ar' ? 'اسحب الصور هنا أو انقر للرفع' : i18n.language === 'de' ? 'Bilder hierher ziehen oder klicken' : 'Drag & drop images or click to upload'}
                        </p>
                        <p className={`text-xs ${textM}`}>PNG, JPG, WEBP</p>
                      </motion.div>
                    </div>

                    {/* Screenshot Previews */}
                    {screenshots.length > 0 && (
                      <div className="flex gap-3 mt-4 flex-wrap">
                        {screenshots.map((ss, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="relative group"
                          >
                            <div className={`w-20 h-20 rounded-lg overflow-hidden border ${isDark ? 'border-white/[0.08]' : 'border-gray-200'}`}>
                              <img src={ss.preview} alt={ss.name} className="w-full h-full object-cover" />
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); removeScreenshot(i); }}
                              className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex gap-3 mt-8">
                  <Button variant="outline" onClick={goBack} className={`flex-1 h-12 rounded-xl ${isDark ? 'border-white/[0.08] text-white/60' : 'border-gray-200 text-gray-500'}`}>
                    <ArrowLeft className="w-4 h-4 mr-2" /> {t('common.back')}
                  </Button>
                  <Button onClick={goNext} disabled={!form.description} className="flex-1 h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-xl">
                    {t('common.next')} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* ═══════════════════════════════════════
             STEP 4: PERSONAL INFORMATION
             ═══════════════════════════════════════ */}
          {step === 4 && (
            <motion.div key="step4" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={pageTransition}>
              <div className={`rounded-2xl p-8 border ${cardBg}`}>
                <div className="space-y-6">
                  <div>
                    <Label className={`text-sm mb-2 block ${textS}`}>
                      <User className="w-3.5 h-3.5 inline mr-1.5" />
                      {t('appeal.fullName')} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      className={`h-12 ${inputCl} rounded-xl`}
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <Label className={`text-sm mb-2 block ${textS}`}>
                      <Mail className="w-3.5 h-3.5 inline mr-1.5" />
                      {t('appeal.email')} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={`h-12 ${inputCl} rounded-xl`}
                      placeholder="you@example.com"
                    />
                  </div>

                  <div>
                    <Label className={`text-sm mb-2 block ${textS}`}>
                      <MapPin className="w-3.5 h-3.5 inline mr-1.5" />
                      {t('appeal.country')} <span className="text-red-500">*</span>
                    </Label>
                    <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                      <SelectTrigger className={`h-12 ${inputCl} rounded-xl`}>
                        <SelectValue placeholder={i18n.language === 'ar' ? 'اختر البلد' : 'Select country'} />
                      </SelectTrigger>
                      <SelectContent className={`${dropCl} max-h-60`}>
                        {COUNTRIES.map(c => (<SelectItem key={c} value={c} className={itemCl}>{c}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-3 mt-8">
                  <Button variant="outline" onClick={goBack} className={`flex-1 h-12 rounded-xl ${isDark ? 'border-white/[0.08] text-white/60' : 'border-gray-200 text-gray-500'}`}>
                    <ArrowLeft className="w-4 h-4 mr-2" /> {t('common.back')}
                  </Button>
                  <Button onClick={goNext} disabled={!form.full_name || !form.email} className="flex-1 h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-xl">
                    {t('common.next')} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* ═══════════════════════════════════════
             STEP 5: PAYMENT SELECTION
             ═══════════════════════════════════════ */}
          {step === 5 && (
            <motion.div key="step5" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={pageTransition}>
              <div className={`rounded-2xl p-8 border ${cardBg}`}>
                {/* Summary Bar */}
                <div className={`rounded-xl p-4 mb-6 border ${isDark ? 'bg-white/[0.02] border-white/[0.04]' : 'bg-gray-50 border-gray-100'}`}>
                  <div className="flex flex-wrap gap-3 items-center">
                    <Sparkles className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
                    <span className={`text-sm ${textS}`}>
                      {form.issue_type} &middot; {form.platform === 'Other' ? otherPlatform : form.platform} &middot; {form.full_name}
                    </span>
                  </div>
                </div>

                <h3 className={`text-lg font-semibold mb-6 ${textP}`} style={{ fontFamily: 'Outfit' }}>{t('plans.title')}</h3>

                {/* Free Appeal Option */}
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => { setUseFree(true); setSelectedPlan(null); }}
                  className={`w-full text-left p-5 rounded-xl border-2 transition-all relative overflow-hidden mb-4 ${
                    useFree
                      ? isDark ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-emerald-50 border-emerald-300'
                      : isDark ? 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                  style={useFree ? { boxShadow: '0 0 30px rgba(16,185,129,0.1)' } : {}}
                >
                  {useFree && <div className="absolute top-3 right-3"><CheckCircle className="w-5 h-5 text-emerald-500" /></div>}
                  <div className="flex items-start gap-4">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                      <Gift className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`font-semibold ${textP}`}>
                          {i18n.language === 'ar' ? 'طلب مجاني' : i18n.language === 'de' ? 'Kostenloser Antrag' : 'Free Appeal'}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`}>
                          {i18n.language === 'ar' ? 'مجاني' : i18n.language === 'de' ? 'Kostenlos' : 'FREE'}
                        </span>
                      </div>
                      <p className={`text-sm ${textS}`}>
                        {i18n.language === 'ar' ? 'قدّم طلبك الأول مجاناً — بدون بطاقة ائتمان' : i18n.language === 'de' ? 'Reichen Sie Ihren ersten Antrag kostenlos ein' : 'Submit your first appeal for free — no credit card required'}
                      </p>
                    </div>
                    <span className="text-2xl font-bold text-emerald-500 flex-shrink-0">$0</span>
                  </div>
                </motion.button>

                {/* Divider */}
                <div className="flex items-center gap-3 my-4">
                  <div className={`flex-1 h-px ${isDark ? 'bg-white/[0.06]' : 'bg-gray-200'}`} />
                  <span className={`text-xs uppercase tracking-wider ${textM}`}>
                    {i18n.language === 'ar' ? 'أو اختر خطة' : i18n.language === 'de' ? 'oder wählen Sie einen Plan' : 'or choose a plan'}
                  </span>
                  <div className={`flex-1 h-px ${isDark ? 'bg-white/[0.06]' : 'bg-gray-200'}`} />
                </div>

                {/* Paid Plans */}
                <div className="space-y-3 mb-6">
                  {plans.map((plan, i) => (
                    <motion.button key={plan.id} whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
                      onClick={() => { setSelectedPlan(plan); setUseFree(false); }}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all relative ${
                        selectedPlan?.id === plan.id && !useFree
                          ? isDark ? 'bg-blue-500/10 border-blue-500/30' : 'bg-blue-50 border-blue-300'
                          : isDark ? 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }`}
                      style={selectedPlan?.id === plan.id && !useFree ? { boxShadow: '0 0 30px rgba(59,130,246,0.1)' } : {}}
                    >
                      {selectedPlan?.id === plan.id && !useFree && <div className="absolute top-3 right-3"><CheckCircle className="w-5 h-5 text-blue-500" /></div>}
                      <div className="flex justify-between items-start">
                        <div className="flex items-start gap-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
                            <CreditCard className="w-4 h-4 text-blue-500" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`font-semibold ${textP}`}>{plan.name}</span>
                              {i === 1 && <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-500 rounded-full">{t('plans.popular')}</span>}
                            </div>
                            <p className={`text-sm mt-1 ${textS}`}>{plan.description}</p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-4">
                          <span className={`text-2xl font-bold ${textP}`}>{plan.currency === 'eur' ? '€' : '$'}{plan.price}</span>
                          <span className={`text-sm ${textM}`}>{plan.billing_cycle === 'one_time' ? '' : t('plans.monthly')}</span>
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>

                {/* Submit */}
                <div className="flex gap-3">
                  <Button variant="outline" onClick={goBack} className={`flex-1 h-12 rounded-xl ${isDark ? 'border-white/[0.08] text-white/60' : 'border-gray-200 text-gray-500'}`}>
                    <ArrowLeft className="w-4 h-4 mr-2" /> {t('common.back')}
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={loading || (!useFree && !selectedPlan)}
                    className={`flex-1 h-14 text-white rounded-xl font-semibold text-base transition-all ${
                      useFree ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-blue-500 hover:bg-blue-600'
                    }`}
                    style={{ boxShadow: useFree ? '0 0 30px rgba(16,185,129,0.2)' : '0 0 30px rgba(59,130,246,0.2)' }}
                  >
                    {loading ? t('common.loading') : useFree ? (i18n.language === 'ar' ? 'إرسال مجاناً' : i18n.language === 'de' ? 'Kostenlos einreichen' : 'Submit for Free') : t('appeal.submit')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
